/**
 * Section Generator (Visualizer v2)
 * =================================
 *
 * Back-compat bridge: converts the existing band-based wall config into the
 * canonical positioned-section model (`RoomSurfaces`). For any wall expressible
 * as today's stacked bands, this emits sections whose W&CB / SlatWall areas
 * exactly mirror the band decomposition — so the section-based calc can
 * reproduce today's bill of materials. The wizard keeps working unchanged; it
 * simply now emits the canonical model through this generator.
 *
 * It reuses `calculateCoreDimensions()` so the band heights (below-SW, SlatWall,
 * above-SW, transition gap) are identical to the current engine by construction.
 *
 * NOTE — garage walls are only PARTIALLY modeled here: the above-garage W&CB
 * area is emitted for panel-count parity, but the garage opening itself and the
 * exact horizontal placement are deferred to the openings phase
 * (see docs/VISUALIZER-V2-PLAN.md §I). Exclude garage configs from regression
 * comparison until then.
 *
 * Branch-exclusive to `3d-visualizer-v3`.
 */

import { WallConfig, ProductColor, PanelOrientation } from '../../types';
import { Surface, Section, RoomSurfaces } from '../../types/sections';
import { calculateCoreDimensions } from '../dimensions';
import { SLATWALL_TRANSITION_SPACE_FEET } from '../constants';

/**
 * Convert a single wall's band config into a positioned-section Surface.
 * Sections use the surface-local bottom-left coordinate system (feet).
 */
export function surfaceFromWallConfig(wall: WallConfig, commonRoomHeight: number): Surface {
  const dims = calculateCoreDimensions(wall, commonRoomHeight);
  const width = dims.wallNetLength; // net of garage door width
  const sections: Section[] = [];

  // W&CB below SlatWall (or full-height W&CB when no SlatWall)
  if (width > 0 && dims.wallheightBelowSW > 0) {
    sections.push({
      id: `${wall.wallId}-wcb-below`,
      productType: 'WCB',
      x: 0,
      y: 0,
      width,
      height: dims.wallheightBelowSW,
      color: wall.color,
      orientation: wall.orientation,
    });
  }

  // SlatWall band (INSERT)
  if (width > 0 && dims.slatWallCalculatedHeight > 0) {
    sections.push({
      id: `${wall.wallId}-slatwall`,
      productType: 'SlatWall',
      x: 0,
      y: wall.slatWall.startHeight,
      width,
      height: dims.slatWallCalculatedHeight,
      color: wall.slatWall.color,
    });
  }

  // W&CB above SlatWall (sits above the 1" transition gap)
  if (width > 0 && dims.wallheightAboveSW > 0) {
    const yAbove =
      wall.slatWall.startHeight + dims.slatWallCalculatedHeight + SLATWALL_TRANSITION_SPACE_FEET;
    sections.push({
      id: `${wall.wallId}-wcb-above`,
      productType: 'WCB',
      x: 0,
      y: yAbove,
      width,
      height: dims.wallheightAboveSW,
      color: wall.color,
      orientation: wall.orientation,
    });
  }

  // Above-garage W&CB — panel-count parity only; placement/opening deferred.
  if (wall.garage.enabled && dims.heightAboveGarage > 0) {
    const garageWidth = wall.garage.width * wall.garage.numDoors;
    if (garageWidth > 0) {
      sections.push({
        id: `${wall.wallId}-wcb-above-garage`,
        productType: 'WCB',
        x: width,
        y: wall.garage.height,
        width: garageWidth,
        height: dims.heightAboveGarage,
        color: wall.color,
        orientation: wall.orientation,
      });
    }
  }

  return {
    id: wall.wallId,
    kind: 'wall',
    name: wall.name,
    width: wall.length,
    height: wall.height,
    sections,
  };
}

/**
 * Build a ceiling Surface as a single full-coverage W&CB fill.
 * `length` × `width` are the ceiling extents in feet.
 */
export function ceilingSurface(
  id: string,
  name: string,
  length: number,
  width: number,
  color: ProductColor,
  orientation: PanelOrientation = 'Horizontal',
): Surface {
  const sections: Section[] = [];
  if (length > 0 && width > 0) {
    sections.push({
      id: `${id}-wcb`,
      productType: 'WCB',
      x: 0,
      y: 0,
      width: length,
      height: width,
      color,
      orientation,
    });
  }
  return { id, kind: 'ceiling', name, width: length, height: width, sections };
}

/**
 * Convert all enabled walls into the canonical RoomSurfaces model.
 * (Ceiling, if any, is added by the caller via `ceilingSurface`.)
 */
export function roomSurfacesFromWallConfigs(
  walls: WallConfig[],
  commonRoomHeight: number,
): RoomSurfaces {
  // Mirror calculateMultipleWalls: each wall uses its OWN height, falling back
  // to commonRoomHeight only when unset. This keeps the section path's core
  // dimensions identical to the current engine for IndividualWalls mode.
  return {
    walls: walls
      .filter((w) => w.enabled)
      .map((w) => surfaceFromWallConfig(w, w.height > 0 ? w.height : commonRoomHeight)),
  };
}
